import { useRef, useState } from "react";
import { motion, useMotionValueEvent, useReducedMotion, useScroll } from "framer-motion";
import { StageScript } from "./StageScript";
import { StageFilm } from "./StageFilm";
import { StageEdit } from "./StageEdit";
import { StageCampaign } from "./StageCampaign";
import { StageLeads } from "./StageLeads";
import { StageFollowUp } from "./StageFollowUp";

export const STAGES = [
  { short: "Script", title: "We Write the Scripts.", text: "We create advertising scripts designed to grab attention and generate customer inquiries.", C: StageScript },
  { short: "Film", title: "We Film the Content.", text: "We show up and film professional advertising content for your business.", C: StageFilm },
  { short: "Edit", title: "We Edit the Ads.", text: "We turn your footage into engaging advertisements designed to get people interested in your services.", C: StageEdit },
  { short: "Launch", title: "We Run Your Campaigns.", text: "We launch, manage, test, and optimize your Facebook and Instagram advertising campaigns.", C: StageCampaign },
  { short: "Leads", title: "You Get the Leads.", text: "We help connect your business with people interested in your services.", C: StageLeads },
  { short: "Follow Up", title: "We Help You Follow Up Automatically.", text: "Respond quickly, stay connected with interested customers, and spend less time chasing leads.", C: StageFollowUp },
];
const EASE = [0.22, 1, 0.36, 1];

function StaticProcess() {
  return (
    <section id="process" className="container-x space-y-20 py-24" data-testid="process-section">
      {STAGES.map(({ title, text, C, short }, i) => (
        <div key={short} className="grid items-center gap-8 lg:grid-cols-[0.9fr_1.2fr]">
          <div>
            <p className="eyebrow">Step {String(i + 1).padStart(2, "0")} / 06</p>
            <h3 className="mt-4 text-3xl font-semibold tracking-tight text-white sm:text-4xl">{title}</h3>
            <p className="mt-4 text-slate-400">{text}</p>
          </div>
          <div className="h-[440px]"><C p={1} /></div>
        </div>
      ))}
    </section>
  );
}

export function ProcessScroll() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const [state, setState] = useState({ i: 0, p: 0 });
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const raw = Math.min(5.999, Math.max(0, v * 6));
    const i = Math.floor(raw);
    const p = Math.round((raw - i) * 100) / 100;
    setState((s) => (s.i === i && s.p === p ? s : { i, p }));
  });

  if (reduce) return <StaticProcess />;

  const jump = (idx) => {
    const el = ref.current;
    const top = el.getBoundingClientRect().top + window.scrollY;
    const target = top + ((el.offsetHeight - window.innerHeight) * (idx + 0.2)) / 6;
    if (window.__lenis) window.__lenis.scrollTo(target, { duration: 1.4 });
    else window.scrollTo({ top: target, behavior: "smooth" });
  };
  const { i, p } = state;
  const overall = (i + p) / 6;

  return (
    <section id="process" ref={ref} className="relative h-[720vh]" data-testid="process-section" aria-label="Our process">
      <div className="sticky top-0 h-[100svh] overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute right-[-10%] top-1/2 h-[700px] w-[700px] -translate-y-1/2 rounded-full bg-electric/[0.13] blur-[150px]" />
        </div>
        <div className="container-x relative grid h-full grid-rows-[auto_1fr] gap-5 pb-6 pt-[84px] lg:grid-cols-[0.85fr_1.2fr] lg:grid-rows-1 lg:items-center lg:gap-16 lg:py-0">
          <div>
            <p className="eyebrow flex flex-wrap items-center gap-x-3 gap-y-2">Our Process — <span className="text-white">{String(i + 1).padStart(2, "0")}</span> / 06
              <span className="rounded-full border border-amber-300/30 px-2.5 py-0.5 text-[10px] tracking-[0.14em] text-amber-200" data-testid="process-example-badge">Example: Realtor ad</span>
            </p>
            <div className="mt-4 grid lg:mt-6" aria-live="polite">
              {STAGES.map((s, idx) => (
                <motion.div key={s.short} className="[grid-area:1/1]" aria-hidden={idx !== i}
                  initial={false} animate={{ opacity: idx === i ? 1 : 0, y: idx === i ? 0 : idx < i ? -28 : 28 }} transition={{ duration: 0.6, ease: EASE }}>
                  <h2 className="text-[1.75rem] !leading-[1.08] sm:text-4xl lg:text-[3.4rem] font-semibold tracking-[-0.035em] text-white">{s.title}</h2>
                  <p className="mt-3 max-w-md text-sm sm:text-base lg:mt-5 lg:text-lg leading-relaxed text-slate-400">{s.text}</p>
                </motion.div>
              ))}
            </div>
            <div className="mt-5 lg:mt-12">
              <div className="h-px w-full bg-white/10"><div className="h-px bg-gradient-to-r from-electric to-cyan-300" style={{ width: `${overall * 100}%` }} /></div>
              <div className="mt-3 flex flex-wrap gap-1.5 lg:gap-2">
                {STAGES.map((s, idx) => (
                  <button key={s.short} onClick={() => jump(idx)} data-testid={`process-step-${idx + 1}-btn`} aria-current={idx === i ? "step" : undefined}
                    className={`rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em] transition-colors duration-300 lg:px-3 lg:py-1.5 lg:text-[11px] ${idx === i ? "border-electric bg-electric/15 text-white" : idx < i ? "border-white/15 text-slate-300" : "border-white/10 text-slate-400 hover:text-white"}`}>
                    {String(idx + 1).padStart(2, "0")} {s.short}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="relative min-h-0 lg:h-[72vh]">
            {STAGES.map(({ C, short }, idx) => {
              if (Math.abs(idx - i) > 1) return null;
              const active = idx === i;
              return (
                <motion.div key={short} className="absolute inset-0 flex items-center justify-center"
                  initial={false}
                  animate={{ opacity: active ? 1 : 0, scale: active ? 1 : idx < i ? 0.9 : 1.06, y: active ? 0 : idx < i ? -40 : 40, filter: active ? "blur(0px)" : "blur(8px)" }}
                  transition={{ duration: 0.7, ease: EASE }} style={{ pointerEvents: active ? "auto" : "none" }}>
                  <C p={active ? p : idx < i ? 1 : 0} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
